import { BREAKPOINTS } from "../config/breakpoints";
import React, { useEffect, useRef, useState } from "react";
import { errorNotification } from "../Generic/Notifications";
import { Table, Tbody, Tcol, Thead, Trow } from "../Generic/Table";
import EmptyState from "../Generic/EmptyState";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as TaskActions from "./actions/taskActions";
import TaskService from "./services";
import { TASK_STATUSES } from "./TaskConstants";
import { beautifyKey } from "../util/util";
import Loader from "../Generic/Loader";
import useWindowSize from "../util/hooks/useWindowSize";
import { formatDisplayDate, LIST_DISPLAY_DATE_FORMAT } from "../util/date.util";

const Page = styled.div`
	display: flex;
	height: max-content;
	overflow-y: auto;
	overflow-x: hidden;
	flex-direction: column;
	text-align: left;
	width: 100%;
	padding: 2rem;
	@media (max-width: ${BREAKPOINTS.tablet}px) {
		padding: 1rem;
	}
`;

const Header = styled.div`
	display: flex;
	font-size: 2rem;
	margin: 0 0 1rem 0.5rem;
	@media (max-width: ${BREAKPOINTS.tablet}px) {
		font-size: 1.5rem;
	}
`;

const CenteredContainer = styled.div`
	width: 100%;
	height: max-content;
	display: flex;
	flex-direction: column;
	align-items: center;
	margin: ${(props) => props.margin};
`;

const TaskListContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	gap: 0.5rem;
`;

const TaskCard = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 0.75rem 1rem;
	align-items: center;
	background: #fff;
	border-radius: 10px;
	padding: 1rem;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
	cursor: pointer;
	transition: box-shadow 0.15s ease;
	&:hover {
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
	}
	& > div:nth-child(1) {
		grid-column: 1;
		grid-row: 1;
	}
	& > div:nth-child(2) {
		grid-column: 1;
		grid-row: 3;
	}
	& > div:nth-child(3) {
		grid-column: 1;
		grid-row: 2;
	}
	& > div:nth-child(4) {
		grid-column: 2;
		grid-row: 1;
		align-items: flex-end;
	}
	& > div:nth-child(5) {
		grid-column: 2;
		grid-row: 2;
		align-items: flex-end;
	}
	& > div:nth-child(6) {
		grid-column: 2;
		grid-row: 3;
		align-items: flex-end;
	}
	& > div:nth-child(n + 4) > div {
		align-self: flex-end;
		text-align: right;
	}
`;

const Cell = styled.div`
	display: flex;
	flex-direction: column;
	min-width: 0;
`;

const CellLabel = styled.span`
	display: block;
	font-size: 0.7rem;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	color: #6b7280;
	margin-bottom: 0.2rem;
`;

const CellValue = styled.span`
	font-size: 0.9rem;
	font-weight: 500;
	color: #111827;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	${(props) =>
		props.noEllipsis &&
		`
		white-space: normal;
		overflow: visible;
		text-overflow: clip;
	`}
`;

const StatusBadge = styled.div`
	align-self: flex-start;
	display: flex;
	align-items: center;
	justify-content: center;
	background: ${(props) => props.background};
	color: ${(props) => props.color};
	font-size: 0.75rem;
	font-weight: 600;
	letter-spacing: 0.03em;
	padding: 0.35rem 0.8rem;
	border-radius: 20px;
	white-space: nowrap;
`;

const TASK_STATUS_META = {
	"1": { backColor: "#f6ea76", textColor: "#776200" },
	"2": { backColor: "#54BF29", textColor: "#ffffff" },
	"3": { backColor: "#008000", textColor: "#ffffff" },
	"4": { backColor: "#d64a4a", textColor: "#ffffff" },
	"5": { backColor: "#d64a4a", textColor: "#ffffff" },
	"6": { backColor: "#d64a4a", textColor: "#ffffff" },
	"10": { backColor: "#d64a4a", textColor: "#ffffff" },
	"11": { backColor: "#3b82f6", textColor: "#ffffff" },
	"12": { backColor: "#6b7280", textColor: "#ffffff" },
};

const getTaskStatusMeta = (status) => ({
	label: TASK_STATUSES[status] || beautifyKey(status),
	...(TASK_STATUS_META[status] || { backColor: "#e5e7eb", textColor: "#374151" }),
});

const DATA_MODES = {
	APPEND: "APPEND",
	REPLACE: "REPLACE",
};

function scrollToRequiredItem(id) {
	const element = document.getElementById(id);
	if (!element) return;
	element.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
}

const TaskList = (props) => {
	const { machineID, kitchenId, taskList, setTaskList, lastClickedElement, clearState, setLastClickedElement } = props;
	const navigate = useNavigate();
	const { state } = useLocation();
	const preserveState = state?.preserveState && taskList?.length ? true : false;
	const [mode, setMode] = useState(preserveState ? DATA_MODES.APPEND : DATA_MODES.REPLACE);
	const [loading, setLoading] = useState(!preserveState);
	const [page, setPage] = useState(1);
	const [lastElement, setLastElement] = useState();
	const hasMore = useRef(true);
	const skipInitialCall = useRef(preserveState);
	const [width] = useWindowSize();
	const isMobile = width < BREAKPOINTS.tablet;

	async function getTaskList(page) {
		setLoading(true);
		const response = await TaskService.getRecentTasks(kitchenId, { page });
		const { data, error } = response;
		if (data) {
			const { tasks, count } = data;

			if (mode === DATA_MODES.APPEND) {
				const totalTasks = [...taskList, ...tasks];
				if (totalTasks.length >= count) {
					hasMore.current = false;
				}
				setTaskList(totalTasks);
			} else {
				if (tasks.length >= count) {
					hasMore.current = false;
				}
				setTaskList(tasks);
			}
		}
		if (error) {
			errorNotification(error);
			console.log(error);
		}
		setLoading(false);
	}

	//Creating a new observer which increments the page number once the observed element is fully visible
	const observer = useRef(
		new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) {
					if (hasMore.current) {
						setMode(DATA_MODES.APPEND);
						setPage((page) => page + 1);
					}
				}
			},
			{
				//Threshold set to 1 for the page to increment only when the element is 100% visible
				//Threshold = % of element to be visible / 100
				threshold: 1,
			}
		)
	);

	useEffect(() => {
		//Update the element on which the call is made and set the observer on it once the list re-renders
		const currentElement = lastElement;
		const currentObserver = observer.current;

		if (currentElement) {
			currentObserver.observe(currentElement);
		}

		//Remove the observer once user exits the page
		return () => {
			if (currentElement) {
				currentObserver.unobserve(currentElement);
			}
		};
	}, [lastElement]);

	useEffect(() => {
		if (preserveState && lastClickedElement) {
			scrollToRequiredItem(lastClickedElement.id);
		} else {
			clearState();
		}
	}, [preserveState]);

	useEffect(() => {
		if (skipInitialCall.current) {
			skipInitialCall.current = false;
			return;
		}
		//Fires everytime page changes
		getTaskList(page);
	}, [page]);

	function navigateToTaskPage(taskId) {
		navigate(`/${machineID}/task/${taskId}`);
	}

	function renderTaskList(tasks, loading) {
		if (tasks.length) {
			if (isMobile) {
				return (
					<TaskListContainer>
						{tasks.map((task, index, array) => {
							const { _id, taskType, taskGroupId, status, actualEndTime, actualStartTime } = task;
							const endTimeString = actualEndTime ? formatDisplayDate(actualEndTime, LIST_DISPLAY_DATE_FORMAT) : "-";
							const startTimeString = actualStartTime ? formatDisplayDate(actualStartTime, LIST_DISPLAY_DATE_FORMAT) : "-";
							const taskStatus = getTaskStatusMeta(status);
							const isLastElement = index === array.length - 1;
							return (
								<TaskCard
									ref={(node) => (isLastElement ? setLastElement(node) : null)}
									id={_id}
									key={_id}
									onClick={() => {
										setLastClickedElement({ id: _id });
										navigateToTaskPage(_id);
									}}>
									<Cell>
										<CellLabel>Task ID</CellLabel>
										<CellValue>{_id}</CellValue>
									</Cell>
									<Cell>
										<CellLabel>Task Group ID</CellLabel>
										<CellValue>{taskGroupId}</CellValue>
									</Cell>
									<Cell>
										<CellLabel>Task Type</CellLabel>
										<CellValue>{beautifyKey(taskType)}</CellValue>
									</Cell>
									<Cell>
										<CellLabel>Task Status</CellLabel>
										<StatusBadge background={taskStatus.backColor} color={taskStatus.textColor}>
											{taskStatus.label}
										</StatusBadge>
									</Cell>
									<Cell>
										<CellLabel>Start Time</CellLabel>
										<CellValue noEllipsis>{startTimeString}</CellValue>
									</Cell>
									<Cell>
										<CellLabel>End Time</CellLabel>
										<CellValue noEllipsis>{endTimeString}</CellValue>
									</Cell>
								</TaskCard>
							);
						})}
					</TaskListContainer>
				);
			}
			return (
				<Table>
					<Trow head>
						<Thead>Task ID</Thead>
						<Thead>Task Type</Thead>
						<Thead>Task Group ID</Thead>
						<Thead>Task Status</Thead>
						<Thead>Start Time</Thead>
						<Thead>End Time</Thead>
					</Trow>
					<Tbody>
						{tasks.map((task, index, array) => {
							const { _id, taskType, taskGroupId, status, actualEndTime, actualStartTime } = task;
							const endTimeString = actualEndTime ? formatDisplayDate(actualEndTime, LIST_DISPLAY_DATE_FORMAT) : "-";
							const startTimeString = actualStartTime ? formatDisplayDate(actualStartTime, LIST_DISPLAY_DATE_FORMAT) : "-";
							return (
								<Trow
									id={_id}
									key={_id}
									innerRef={(node) => (index === array.length - 1 ? setLastElement(node) : null)}
									onClick={() => {
										setLastClickedElement({ id: _id });
										navigateToTaskPage(_id);
									}}>
									<Tcol borderRadius="10px 0 0 10px">{_id}</Tcol>
									<Tcol>{beautifyKey(taskType)}</Tcol>
									<Tcol>{taskGroupId}</Tcol>
									<Tcol>{TASK_STATUSES[status] || beautifyKey(status)}</Tcol>
									<Tcol>{startTimeString}</Tcol>
									<Tcol borderRadius="0 10px 10px 0">{endTimeString}</Tcol>
								</Trow>
							);
						})}
					</Tbody>
				</Table>
			);
		} else if (!loading) {
			return <EmptyState retry={getTaskList} />;
		}
	}

	return (
		<Page>
			<Header>Recent Tasks</Header>
			{renderTaskList(taskList, loading)}
			{loading ? (
				<CenteredContainer>
					<Loader />
				</CenteredContainer>
			) : (
				""
			)}
		</Page>
	);
};

const mapStateToProps = (state) => {
	const { task, landing } = state;
	return { ...task, ...landing };
};

const mapDispatchToProps = (dispatch) => ({
	...bindActionCreators(
		{
			...TaskActions,
		},
		dispatch
	),
});

export default connect(mapStateToProps, mapDispatchToProps)(TaskList);
